import * as THREE from 'three';
import {isSketchEllipseObject} from '../../../mesh/guards/isSketchEllipseObject';
import {AbstractSketchNearestPointProjector} from './AbstractSketchNearestPointProjector';
import {SketchParametricCurveNearestPointSolver} from './SketchParametricCurveNearestPointSolver';
import {SketchEllipseParametricGeometryService} from '../../../sketch/geometry/SketchEllipseParametricGeometryService';

/**
 * 草图椭圆族最近点投影器。
 *
 * 职责：对完整椭圆和有限椭圆弧使用真实椭圆参数方程求解，不使用显示采样弦。
 */
export class SketchEllipseNearestPointProjector extends AbstractSketchNearestPointProjector {
    /** 统一参数曲线最近点求解器。 */
    private readonly solver = new SketchParametricCurveNearestPointSolver();

    /** 权威椭圆参数几何服务。 */
    private readonly ellipseGeometryService: SketchEllipseParametricGeometryService;

    /** 复用的鼠标平面投影坐标，避免法向距离降低最近参数比较精度。 */
    private readonly projectedWorldPosition = new THREE.Vector3();

    /**
     * 创建草图椭圆最近点投影器。
     * @param ellipseGeometryService 权威椭圆参数几何服务。
     */
    public constructor(
        ellipseGeometryService: SketchEllipseParametricGeometryService = new SketchEllipseParametricGeometryService(),
    ) {
        super();
        this.ellipseGeometryService = ellipseGeometryService;
    }

    /**
     * 判断目标是否为草图椭圆或椭圆弧。
     * @param targetObject 待判断对象。
     * @returns 目标为椭圆族对象时返回 true。
     */
    public supports(targetObject: THREE.Object3D): boolean {
        return isSketchEllipseObject(targetObject);
    }

    /**
     * 将世界坐标投影到真实椭圆参数曲线。
     * @param targetObject 目标椭圆族对象。
     * @param worldPosition 待投影世界坐标。
     * @returns 椭圆有限参数域上的最近世界坐标。
     */
    public project(targetObject: THREE.Object3D, worldPosition: THREE.Vector3): THREE.Vector3 | null {
        if (!isSketchEllipseObject(targetObject)) {
            return null;
        }

        const definition = targetObject.getEllipseDefinition();
        if (definition.radiusX <= 0 || definition.radiusY <= 0) {
            return null;
        }

        const isClosedEllipse = this.ellipseGeometryService.isFullEllipse(definition);
        targetObject.referencePlane.projectWorldPointToPlaneTo(
            worldPosition,
            this.projectedWorldPosition,
        );

        return this.solver.solve(
            this.projectedWorldPosition,
            0,
            1,
            (parameter, target) => {
                // 最近点求解只搜索参数，实际点始终由权威椭圆参数服务求值。
                return this.ellipseGeometryService.evaluateWorldPointAtParameter(
                    targetObject.referencePlane,
                    definition,
                    parameter,
                    target,
                );
            },
            96,
            isClosedEllipse,
        );
    }

}
